import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, AlertCircle, UserPlus, Briefcase, X, MessageSquare, Mail, CheckCircle2, Sparkles,
} from "lucide-react";
import { api } from "../lib/api";
import { formatIST, formatRelativeIST } from "../lib/time";
import { useAuth } from "../contexts/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";

const ICONS = {
  new_user: UserPlus,
  new_job_open: Briefcase,
  new_job_closed: X,
  new_inquiry: MessageSquare,
  verification_pending: Mail,
  email_verified: CheckCircle2,
  job_match: Sparkles,
};

const LABELS = {
  new_user: (n) => `${n.name || n.email} signed up as ${n.role}`,
  new_job_open: (n) => `New job posted: "${n.job_title}" at ${n.company_name}`,
  new_job_closed: (n) => `Job closed: "${n.job_title}" at ${n.company_name}`,
  new_inquiry: (n) => `Inquiry from ${n.name}: ${n.purpose_label}`,
  verification_pending: () => "Please verify your email to unlock resume upload, job apply and AI analysis",
  email_verified: () => "Your email is verified — all features are now unlocked",
  job_match: (n) => `${n.match_score}% match: "${n.job_title}" at ${n.company_name}`,
};

const NAV_ON_CLICK = {
  new_user: () => "/admin",
  new_job_open: () => "/admin",
  new_job_closed: () => "/admin",
  new_inquiry: () => "/admin",
  verification_pending: () => "/dashboard",
  email_verified: () => "/dashboard",
  job_match: (n) => (n.job_id ? `/jobs/${n.job_id}` : "/jobs"),
};

export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const isAdmin = user?.role === "admin";
  const endpoint = isAdmin ? "/admin/notifications" : "/notifications";
  const markAllEndpoint = isAdmin
    ? "/admin/notifications/mark-all-read"
    : "/notifications/mark-all-read";
  const markOneEndpoint = (nid) =>
    isAdmin
      ? `/admin/notifications/${nid}/read`
      : `/notifications/${nid}/read`;

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get(endpoint);
      setItems((data?.items || []).slice(0, 15));
      setUnread(data?.unread || 0);
    } catch {
      // silent
    }
  }, [user, endpoint]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const markAllRead = async () => {
    try {
      await api.post(markAllEndpoint);
      load();
    } catch {
      // ignore
    }
  };

  const handleItemClick = async (n) => {
    setOpen(false);
    // best-effort mark as read
    if (!n.read) {
      try {
        await api.post(markOneEndpoint(n.notification_id));
      } catch {
        // ignore
      }
    }
    const target = (NAV_ON_CLICK[n.type] || (() => (isAdmin ? "/admin" : "/dashboard")))(n);
    navigate(target);
  };

  if (!user) return null;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="notification-bell"
          aria-label="Notifications"
          className="relative h-9 w-9 rounded-md hover:bg-zinc-100 grid place-items-center transition-colors"
        >
          <Bell className="h-4 w-4 text-zinc-700" />
          {unread > 0 && (
            <span
              data-testid="notification-unread-badge"
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold tag-mono grid place-items-center"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between p-3 border-b border-zinc-100">
          <div>
            <div className="font-display font-bold text-sm">
              {isAdmin ? "Pending tasks" : "Notifications"}
            </div>
            <div className="text-xs text-zinc-500 tag-mono" data-testid="bell-unread-count">
              {unread} unread
            </div>
          </div>
          {unread > 0 && (
            <Button size="sm" variant="ghost" onClick={markAllRead} data-testid="bell-mark-read">
              Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-8 text-center text-sm text-zinc-500">You&apos;re all caught up.</div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {items.map((n) => {
                const Icon = ICONS[n.type] || AlertCircle;
                return (
                  <li
                    key={n.notification_id}
                    className={`p-3 flex gap-3 hover:bg-zinc-50 transition-colors cursor-pointer ${n.read ? "opacity-60" : ""}`}
                    onClick={() => handleItemClick(n)}
                    data-testid={`bell-item-${n.notification_id}`}
                  >
                    <span className={`h-8 w-8 rounded-md grid place-items-center flex-shrink-0 ${
                      n.type === "email_verified"
                        ? "bg-emerald-50 text-emerald-700"
                        : n.type === "verification_pending"
                        ? "bg-amber-50 text-amber-700"
                        : n.type === "job_match"
                        ? "bg-blue-50 text-[#002FA7]"
                        : "bg-blue-50 text-[#002FA7]"
                    }`}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-zinc-950 leading-snug">
                        {(LABELS[n.type] || ((x) => x.type))(n)}
                      </div>
                      <div className="text-[11px] text-zinc-500 tag-mono mt-0.5" title={formatIST(n.created_at)}>
                        {formatRelativeIST(n.created_at)}
                      </div>
                    </div>
                    {!n.read && <span className="h-2 w-2 rounded-full bg-[#002FA7] flex-shrink-0 mt-1" />}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        {isAdmin && (
          <div className="p-2 border-t border-zinc-100">
            <Button
              size="sm"
              variant="ghost"
              className="w-full justify-center"
              onClick={() => { setOpen(false); navigate("/admin"); }}
              data-testid="bell-view-all"
            >
              Open admin panel
            </Button>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
